import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import useAuthStore from './context/authStore';
import { LoaderProvider } from './components/shared/LoaderContext';

// Auth Pages
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';

// Member Pages
import MemberDashboard from './pages/member/MemberDashboard';
import MemberRegistrationPage from './pages/member/MemberRegistrationPage';
import MemberProfilePage from './pages/member/MemberProfilePage';
import ReferralRequestsPage from './pages/member/ReferralRequestsPage';
import DirectoryPage from './pages/member/DirectoryPage';

// Admin Pages
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminMembersPage from './pages/admin/AdminMembersPage';
import AdminMemberDetailPage from './pages/admin/AdminMemberDetailPage';
import AdminReportsPage from './pages/admin/AdminReportsPage';
import AdminEmailConfigPage from './pages/admin/AdminEmailConfigPage';

// Layout
import MemberLayout from './components/shared/MemberLayout';
import AdminLayout from './components/shared/AdminLayout';

const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, initialized } = useAuthStore();
  if (!initialized) return <div className="loading-center"><div className="spinner" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && user.role !== 'ADMIN') return <Navigate to="/dashboard" replace />;
  return children;
};

const PublicRoute = ({ children }) => {
  const { user } = useAuthStore();
  if (user) return <Navigate to={user.role === 'ADMIN' ? '/admin' : '/dashboard'} replace />;
  return children;
};

export default function App() {
  const init = useAuthStore((s) => s.init);
  useEffect(() => { init(); }, [init]);

  return (
    // Wrap entire app with LoaderProvider
    <LoaderProvider>
      <BrowserRouter>
        <Toaster position="top-right" toastOptions={{ duration: 4000 }} />
        <Routes>
          {/* Public */}
          <Route path="/" element={<Navigate to="/login" replace />} />
          <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
          <Route path="/signup" element={<PublicRoute><SignupPage /></PublicRoute>} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />

          {/* Member */}
          <Route path="/" element={<ProtectedRoute><MemberLayout /></ProtectedRoute>}>
            <Route path="dashboard" element={<MemberDashboard />} />
            <Route path="register-membership" element={<MemberRegistrationPage />} />
            <Route path="profile" element={<MemberProfilePage />} />
            <Route path="referral-requests" element={<ReferralRequestsPage />} />
            <Route path="directory" element={<DirectoryPage />} />
          </Route>

          {/* Admin */}
          <Route path="/admin" element={<ProtectedRoute adminOnly><AdminLayout /></ProtectedRoute>}>
            <Route index element={<AdminDashboard />} />
            <Route path="members" element={<AdminMembersPage />} />
            <Route path="members/:id" element={<AdminMemberDetailPage />} />
            <Route path="reports" element={<AdminReportsPage />} />
            <Route path="email-config" element={<AdminEmailConfigPage />} />
          </Route>

          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </BrowserRouter>
    </LoaderProvider>
  );
}
